import { motion, animate } from "framer-motion";
import { useState, useEffect } from "react";
import { useAppStore } from "../stores/useAppStore";

const stripHtml = (html) => {
  return html.replace(/<\/?[^>]+(>|$)/g, "");
};

const headerVariants = {
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.4, ease: "easeInOut" }
  },
  hidden: { 
    opacity: 0, 
    y: "-100%",
    transition: { duration: 0.3, ease: "easeInOut" }
  },
  exit: { 
    opacity: 0, 
    y: "-100%",
    transition: { duration: 0.3, ease: "easeInOut" }
  }
};

export default function Header({ title }) {
  const { hideHeader } = useAppStore();
  const safeTitle = title ? stripHtml(title) : "";
  const [displayText, setDisplayText] = useState(safeTitle);
  const [textProgress, setTextProgress] = useState(safeTitle.length);
  const [isRemoving, setIsRemoving] = useState(false);

  useEffect(() => {
    let controls;

    if (isRemoving) {
      controls = animate(textProgress, 0, {
        duration: 0.4,
        ease: "easeInOut",
        onUpdate: (latest) => setTextProgress(Math.round(latest)),
        onComplete: () => {
          setIsRemoving(false);
          setDisplayText(safeTitle);
        },
      });
    } else {
      controls = animate(0, safeTitle.length, {
        duration: 0.6,
        ease: "easeInOut",
        onUpdate: (latest) => setTextProgress(Math.round(latest)),
      });
    }

    return () => controls?.stop();
  }, [safeTitle, isRemoving]);

  useEffect(() => {
    if (safeTitle !== displayText) {
      setIsRemoving(true);
      setTextProgress(displayText.length);
    }
  }, [safeTitle]);

  return (
    <motion.div
      className="
        absolute top-0 left-0 w-full 
        h-[var(--header-height)] 
        flex
        bg-navy 
        border-b-[0.25rem] border-[#21346b]
        z-10
        pl-[40.375rem]
      "
      variants={headerVariants}
      initial="visible"
      animate={hideHeader ? "hidden" : "visible"}
      exit="exit"
      layout
    >
      <div className="relative h-full flex flex-col items-center">
        <motion.span
          className="
            inline-block font-extralight text-[6.25rem] transition-colors duration-300
            leading-none
            pt-[6.375rem]
            translate-gpu
          "
          key={displayText}
          initial={{ opacity: 0.5, scale: 1.1 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
        >
          {displayText.slice(0, textProgress)}
        </motion.span>
      </div>
    </motion.div>
  );
}